import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { MessageSquare } from "lucide-react";

import { fetchAll, LOG_MAX_ROWS } from "@/lib/fetchAll";
import { TruncationNotice } from "@/components/TruncationNotice";
import { DataTable } from "@/components/data-table/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_app/chatbot/conversations/")({ component: ConversationsPage });

interface SessionRow {
  id: string;
  started_at: number;
  ended_at: number | null;
  locale: string;
  satisfaction: number | null;
  flagged_count: number;
}

function ConversationsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["chatbot", "conversations"],
    // Visitor sessions accumulate on their own, so bound the window rather than
    // page through the whole history. Was capped at 200 with 232 already stored.
    queryFn: () => fetchAll<SessionRow>("/admin/chatbot/conversations", { maxRows: LOG_MAX_ROWS }),
  });

  const columns: ColumnDef<SessionRow, unknown>[] = [
    {
      accessorKey: "started_at", header: "Started",
      cell: ({ row }) => format(row.original.started_at * 1000, "MMM d, HH:mm"),
    },
    {
      id: "duration", header: "Duration",
      cell: ({ row }) => row.original.ended_at
        ? `${Math.round((row.original.ended_at - row.original.started_at) / 60)} min`
        : <span className="text-muted-foreground">ongoing</span>,
    },
    {
      accessorKey: "locale", header: "Locale",
      cell: ({ row }) => <Badge variant="outline">{row.original.locale}</Badge>,
    },
    {
      accessorKey: "satisfaction", header: "Rating",
      cell: ({ row }) => row.original.satisfaction != null
        ? <span>{"★".repeat(row.original.satisfaction)}{"☆".repeat(5 - row.original.satisfaction)}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      accessorKey: "flagged_count", header: "Flags",
      cell: ({ row }) => row.original.flagged_count > 0
        ? <Badge variant="destructive">{row.original.flagged_count}</Badge>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      id: "actions", header: "",
      cell: ({ row }) => (
        <Link to="/chatbot/conversations/$id" as never params={{ id: row.original.id }}
          className="text-sm text-primary hover:underline">
          <MessageSquare className="inline h-3.5 w-3.5" /> View
        </Link>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Chatbot conversations"
        description="Recent visitor sessions. Click into one to read the full transcript with cited sources."
      />
      {data?.truncated && <TruncationNotice count={data.items.length} noun="sessions" />}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by session id…"
        emptyMessage={isLoading ? "Loading…" : "No conversations yet."}
      />
    </div>
  );
}
